import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Camera, Image, Zap, Sunset, Home as HomeIcon, Trash2, Sofa, Video, CheckCircle, ArrowLeft, X } from 'lucide-react';
import { services, editingProcessSteps, serviceDescriptions } from '../mock';
import { videoFileIds } from '../videoData';
import DraggableBeforeAfter from '../components/DraggableBeforeAfter';
import RevealSection from '../components/RevealSection';

const iconMap = { Camera, Image, Zap, Sunset, Home: HomeIcon, Trash2, Sofa, Video };

const Services = () => {
  const { serviceId } = useParams();
  const [zoomedImage, setZoomedImage] = useState(null);
  const service = services.find(s => s.id === serviceId);
  const processSteps = serviceId ? editingProcessSteps[serviceId] : null;
  const description = serviceId ? serviceDescriptions[serviceId] : null;

  if (!service) {
    return (
      <div className="bg-[#0A0B0E] min-h-screen pt-32 px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-serif text-[#D4AF37] text-4xl mb-4">Service Not Found</h1>
          <Link to="/" className="text-[#D4AF37] hover:underline font-sans text-sm">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0A0B0E] min-h-screen">
      {/* Header */}
      <section className="pt-24 pb-12 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <Link to="/#services" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] mb-8 transition-colors font-sans text-sm">
            <ArrowLeft size={16} /> Back to Portfolio
          </Link>

          <p className="text-[11px] uppercase tracking-[0.3em] font-sans font-medium text-[#D4AF37] mb-3">Service Detail</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-[#F8FAFC] mb-4">{service.title}</h1>
          <div className="w-16 h-0.5 bg-gradient-to-r from-[#D4AF37] to-transparent mb-6" />
          {description && <p className="text-sm text-[#94A3B8] font-sans leading-relaxed max-w-3xl">{description}</p>}
        </div>
      </section>

      {/* Process Steps */}
      {processSteps && (
        <section className="pb-16 px-5 md:px-8">
          <div className="max-w-7xl mx-auto">
            <RevealSection>
              <h2 className="font-serif text-2xl text-[#F8FAFC] mb-8">{processSteps.title}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {processSteps.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 rounded-lg border border-[rgba(255,255,255,0.05)] bg-[#12141A] group hover:border-[#D4AF37]/30 transition-colors duration-300">
                    <div className="w-7 h-7 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 text-xs font-sans font-bold text-[#D4AF37]">
                      {idx + 1}
                    </div>
                    <p className="text-sm text-[#94A3B8] font-sans pt-0.5 group-hover:text-[#F8FAFC] transition-colors duration-300">{step}</p>
                  </div>
                ))}
              </div>
            </RevealSection>
          </div>
        </section>
      )}

      {/* Work Examples */}
      <section className="pb-16 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          {service.id === 'video' ? (
            <>
              <h2 className="font-serif text-2xl text-[#F8FAFC] mb-8">Our Video Work</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {videoFileIds.map((video, idx) => (
                  <div key={idx} className="rounded-xl overflow-hidden border border-[rgba(255,255,255,0.06)]">
                    <iframe
                      src={`https://drive.google.com/file/d/${video.id}/preview`}
                      className="w-full aspect-video"
                      allow="autoplay"
                      allowFullScreen
                      title={video.title}
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h2 className="font-serif text-2xl text-[#F8FAFC] mb-8">Before & After Examples</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {service.images && service.images.map((image, idx) => (
                  <RevealSection key={idx} delay={idx * 50}>
                    <div className="cursor-pointer" onClick={() => setZoomedImage(image)}>
                      <DraggableBeforeAfter beforeImage={image.before} afterImage={image.after} title={image.title} />
                      <p className="text-xs text-[#64748B] font-sans mt-2">{image.title}</p>
                    </div>
                  </RevealSection>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-5 md:px-8 bg-[#12141A]">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-2xl text-[#F8FAFC] mb-8">What's Included</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {service.features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-lg border border-[rgba(255,255,255,0.05)] bg-[#161922]">
                <CheckCircle className="text-[#D4AF37] flex-shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-[#94A3B8] font-sans">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-5 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl text-[#F8FAFC] mb-4">Ready to Get Started?</h2>
          <p className="text-sm text-[#94A3B8] font-sans mb-8">Send us your photos and we'll deliver stunning results.</p>
          <Link to="/#contact" className="inline-flex items-center px-8 py-3.5 bg-[#D4AF37] text-[#0A0B0E] rounded-full font-sans font-semibold text-sm tracking-wide btn-premium">
            Get In Touch
          </Link>
        </div>
      </section>

      {/* Zoom Modal */}
      {zoomedImage && (
        <div className="fixed inset-0 z-50 bg-[#0A0B0E]/95 flex items-center justify-center p-4" onClick={() => setZoomedImage(null)} data-testid="zoom-modal">
          <button onClick={() => setZoomedImage(null)} className="absolute top-5 right-5 text-white/60 hover:text-white z-50 p-2">
            <X size={24} />
          </button>
          <div className="max-w-6xl w-full grid grid-cols-1 md:grid-cols-2 gap-2" onClick={e => e.stopPropagation()}>
            <div className="relative rounded-lg overflow-hidden bg-[#0A0B0E]">
              <img src={zoomedImage.before} alt="Before" className="w-full h-[40vh] md:h-[70vh] object-contain" referrerPolicy="no-referrer" />
              <div className="absolute top-4 left-4 bg-[#0A0B0E]/80 text-white px-4 py-1.5 rounded-full text-xs font-sans font-bold uppercase tracking-wider border border-white/10">Before</div>
            </div>
            <div className="relative rounded-lg overflow-hidden bg-[#0A0B0E]">
              <img src={zoomedImage.after} alt="After" className="w-full h-[40vh] md:h-[70vh] object-contain" referrerPolicy="no-referrer" />
              <div className="absolute top-4 right-4 bg-[#D4AF37] text-[#0A0B0E] px-4 py-1.5 rounded-full text-xs font-sans font-bold uppercase tracking-wider">After</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
